/**
 * Corrective action report (CAR) helpers — ISO 9001 clause 10.2 workflow.
 */

import type { Pool, PoolClient } from "pg";

export const CORRECTIVE_ACTION_STATUSES = [
  "draft",
  "open",
  "containment",
  "analysis",
  "action",
  "verification",
  "closed",
  "cancelled",
] as const;

export type CorrectiveActionStatus = (typeof CORRECTIVE_ACTION_STATUSES)[number];

export const CORRECTIVE_ACTION_SOURCES = [
  "customer_complaint",
  "rma",
  "internal_audit",
  "qc_incident",
  "supplier",
  "incoming_quality",
  "management_review",
  "process_deviation",
  "other",
] as const;

export type CorrectiveActionSource = (typeof CORRECTIVE_ACTION_SOURCES)[number];

export interface CorrectiveActionReportRow {
  id: string | number;
  car_number: string | null;
  title: string;
  status: CorrectiveActionStatus;
  source_type: CorrectiveActionSource;
  opened_at: string | Date;
  target_close_date: string | Date | null;
  closed_at: string | Date | null;
  reported_by: string;
  reported_by_display: string | null;
  owner_user_id: string | null;
  owner_display: string | null;
  problem_description: string;
  immediate_containment: string | null;
  root_cause_analysis: string | null;
  corrective_action_plan: string | null;
  implementation_notes: string | null;
  effectiveness_verification: string | null;
  continued_review_on: string | Date | null;
  preventive_action: string | null;
  client_fs_account_id: string | number | null;
  client_name: string | null;
  sales_order_id: string | number | null;
  sales_order_reference: string | null;
  quality_incident_id: string | number | null;
  quality_event_id: string | number | null;
  quality_event_summary: string | null;
  quality_case_id: string | number | null;
  quality_case_title: string | null;
  serial_fci_id: string | number | null;
  device_id: string | number | null;
  device_label_serial: string | null;
  closed_by: string | null;
  closed_by_display: string | null;
  verified_by: string | null;
  verified_by_display: string | null;
  verified_at: string | Date | null;
  created_at: string | Date;
  updated_at: string | Date;
}

export interface CorrectiveActionActivityRow {
  id: string | number;
  corrective_action_report_id: string | number;
  activity_at: string | Date;
  actor_email: string;
  actor_display: string | null;
  action_type: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  note: string | null;
}

export interface CorrectiveActionReport {
  id: number;
  carNumber: string;
  title: string;
  status: CorrectiveActionStatus;
  sourceType: CorrectiveActionSource;
  openedAt: string;
  targetCloseDate: string | null;
  closedAt: string | null;
  reportedBy: string;
  reportedByDisplay: string | null;
  ownerUserId: string | null;
  ownerDisplay: string | null;
  problemDescription: string;
  immediateContainment: string | null;
  rootCauseAnalysis: string | null;
  correctiveActionPlan: string | null;
  implementationNotes: string | null;
  effectivenessVerification: string | null;
  continuedReviewOn: string | null;
  preventiveAction: string | null;
  clientFsAccountId: number | null;
  clientName: string | null;
  salesOrderId: number | null;
  salesOrderReference: string | null;
  qualityIncidentId: number | null;
  qualityEventId: number | null;
  qualityEventSummary: string | null;
  qualityCaseId: number | null;
  qualityCaseTitle: string | null;
  serialFciId: number | null;
  deviceId: number | null;
  deviceLabelSerial: string | null;
  closedBy: string | null;
  closedByDisplay: string | null;
  verifiedBy: string | null;
  verifiedByDisplay: string | null;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CorrectiveActionActivity {
  id: number;
  correctiveActionReportId: number;
  activityAt: string;
  actorEmail: string;
  actorDisplay: string | null;
  actionType: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  note: string | null;
}

const STATUS_SET = new Set<string>(CORRECTIVE_ACTION_STATUSES);
const SOURCE_SET = new Set<string>(CORRECTIVE_ACTION_SOURCES);

const DEFAULT_LIST_LIMIT = 100;
const MAX_LIST_LIMIT = 500;

export const CORRECTIVE_ACTION_USER_JOINS = `
  left join auth.users reported_auth_user
    on lower(trim(reported_auth_user.email)) = lower(trim(quality.corrective_action_report.reported_by))
  left join public.user_setting reported_user_setting
    on reported_user_setting.id = reported_auth_user.id
  left join public.user_setting owner_user_setting
    on owner_user_setting.id = quality.corrective_action_report.owner_user_id
  left join auth.users owner_auth_user
    on owner_auth_user.id = quality.corrective_action_report.owner_user_id
  left join auth.users closed_auth_user
    on lower(trim(closed_auth_user.email)) = lower(trim(quality.corrective_action_report.closed_by))
  left join public.user_setting closed_user_setting
    on closed_user_setting.id = closed_auth_user.id
  left join auth.users verified_auth_user
    on lower(trim(verified_auth_user.email)) = lower(trim(quality.corrective_action_report.verified_by))
  left join public.user_setting verified_user_setting
    on verified_user_setting.id = verified_auth_user.id
`;

export const CORRECTIVE_ACTION_ACTIVITY_USER_JOINS = `
  left join auth.users activity_auth_user
    on lower(trim(activity_auth_user.email)) = lower(trim(quality.corrective_action_activity.actor_email))
  left join public.user_setting activity_user_setting
    on activity_user_setting.id = activity_auth_user.id
`;

function isoTimestampText(value: string | Date | null | undefined): string | null {
  if (value == null) return null;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function optionalPgId(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseCorrectiveActionStatus(value: unknown): CorrectiveActionStatus | undefined {
  if (value == null || value === "") return undefined;
  if (typeof value !== "string") {
    throw new Error("status must be a string.");
  }
  const normalized = value.trim().toLowerCase();
  if (!STATUS_SET.has(normalized)) {
    throw new Error(`status must be one of: ${CORRECTIVE_ACTION_STATUSES.join(", ")}.`);
  }
  return normalized as CorrectiveActionStatus;
}

export function parseCorrectiveActionSource(value: unknown): CorrectiveActionSource {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error("sourceType is required.");
  }
  const normalized = value.trim().toLowerCase();
  if (!SOURCE_SET.has(normalized)) {
    throw new Error(`sourceType must be one of: ${CORRECTIVE_ACTION_SOURCES.join(", ")}.`);
  }
  return normalized as CorrectiveActionSource;
}

export function parseCorrectiveActionLimit(value: unknown): number {
  if (value == null || value === "") return DEFAULT_LIST_LIMIT;
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed < 1) return DEFAULT_LIST_LIMIT;
  return Math.min(parsed, MAX_LIST_LIMIT);
}

export function parseCorrectiveActionOffset(value: unknown): number {
  if (value == null || value === "") return 0;
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

export function trimRequiredText(value: unknown, label: string, max = 20_000): string {
  if (typeof value !== "string") {
    throw new Error(`${label} must be a string.`);
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    throw new Error(`${label} is required.`);
  }
  if (trimmed.length > max) {
    throw new Error(`${label} is too long (max ${max} characters).`);
  }
  return trimmed;
}

export function trimOptionalText(value: unknown, label: string, max = 20_000): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string") {
    throw new Error(`${label} must be a string or null.`);
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > max) {
    throw new Error(`${label} is too long (max ${max} characters).`);
  }
  return trimmed;
}

const INTAKE_VOLTAGE_RE = /^\d+(\.\d{1,3})?$/;

export function parseOptionalVoltage(value: unknown, label: string): string | null {
  const text = trimOptionalText(value, label, 16);
  if (text == null) return null;
  if (!INTAKE_VOLTAGE_RE.test(text)) {
    throw new Error(`${label} must be a number such as 3.65.`);
  }
  return text;
}

export function parseOptionalDate(value: unknown, label: string): string | null {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value !== "string") {
    throw new Error(`${label} must be a date string or null.`);
  }
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    throw new Error(`${label} must use YYYY-MM-DD format.`);
  }
  return trimmed;
}

export function parseOptionalId(value: unknown, label: string): number | null {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) {
    throw new Error(`${label} must be a positive integer or null.`);
  }
  return parsed;
}

export function correctiveActionLabel(id: number): string {
  return `CAR ${id}`;
}

export function appendCorrectiveActionSearch(
  conditions: string[],
  params: unknown[],
  paramIndex: number,
  search: string,
): number {
  const like = `%${search}%`;
  const idMatch = /^(?:CAR\s+)?(\d+)$/i.exec(search.trim());
  if (idMatch != null) {
    conditions.push(
      `(quality.corrective_action_report.id = $${paramIndex}
        or quality.corrective_action_report.title ilike $${paramIndex + 1}
        or quality.corrective_action_report.problem_description ilike $${paramIndex + 1})`,
    );
    params.push(Number(idMatch[1]), like);
    return paramIndex + 2;
  }
  conditions.push(
    `(quality.corrective_action_report.title ilike $${paramIndex}
      or quality.corrective_action_report.problem_description ilike $${paramIndex})`,
  );
  params.push(like);
  return paramIndex + 1;
}

export function mapCorrectiveActionReportRow(row: CorrectiveActionReportRow): CorrectiveActionReport {
  const id = Number(row.id);
  return {
    id,
    carNumber: correctiveActionLabel(id),
    title: row.title,
    status: row.status,
    sourceType: row.source_type,
    openedAt: isoTimestampText(row.opened_at) ?? "",
    targetCloseDate: row.target_close_date == null ? null : String(row.target_close_date).slice(0, 10),
    closedAt: isoTimestampText(row.closed_at),
    reportedBy: row.reported_by,
    reportedByDisplay: row.reported_by_display,
    ownerUserId: row.owner_user_id,
    ownerDisplay: row.owner_display,
    problemDescription: row.problem_description,
    immediateContainment: row.immediate_containment,
    rootCauseAnalysis: row.root_cause_analysis,
    correctiveActionPlan: row.corrective_action_plan,
    implementationNotes: row.implementation_notes,
    effectivenessVerification: row.effectiveness_verification,
    continuedReviewOn: row.continued_review_on == null ? null : String(row.continued_review_on).slice(0, 10),
    preventiveAction: row.preventive_action,
    clientFsAccountId: optionalPgId(row.client_fs_account_id),
    clientName: row.client_name,
    salesOrderId: optionalPgId(row.sales_order_id),
    salesOrderReference: row.sales_order_reference,
    qualityIncidentId: optionalPgId(row.quality_incident_id),
    qualityEventId: optionalPgId(row.quality_event_id),
    qualityEventSummary: row.quality_event_summary,
    qualityCaseId: optionalPgId(row.quality_case_id),
    qualityCaseTitle: row.quality_case_title,
    serialFciId: optionalPgId(row.serial_fci_id),
    deviceId: optionalPgId(row.device_id),
    deviceLabelSerial: row.device_label_serial,
    closedBy: row.closed_by,
    closedByDisplay: row.closed_by_display,
    verifiedBy: row.verified_by,
    verifiedByDisplay: row.verified_by_display,
    verifiedAt: isoTimestampText(row.verified_at),
    createdAt: isoTimestampText(row.created_at) ?? "",
    updatedAt: isoTimestampText(row.updated_at) ?? "",
  };
}

export function mapCorrectiveActionActivityRow(row: CorrectiveActionActivityRow): CorrectiveActionActivity {
  return {
    id: Number(row.id),
    correctiveActionReportId: Number(row.corrective_action_report_id),
    activityAt: isoTimestampText(row.activity_at) ?? "",
    actorEmail: row.actor_email,
    actorDisplay: row.actor_display,
    actionType: row.action_type,
    fieldName: row.field_name,
    oldValue: row.old_value,
    newValue: row.new_value,
    note: row.note,
  };
}

export function correctiveActionStatusLabel(status: CorrectiveActionStatus): string {
  switch (status) {
    case "draft":
      return "Draft";
    case "open":
      return "Open";
    case "containment":
      return "Containment";
    case "analysis":
      return "Root cause analysis";
    case "action":
      return "Corrective action";
    case "verification":
      return "Verification";
    case "closed":
      return "Closed";
    case "cancelled":
      return "Cancelled";
    default:
      return status;
  }
}

export function correctiveActionSourceLabel(source: CorrectiveActionSource): string {
  switch (source) {
    case "customer_complaint":
      return "Customer complaint";
    case "rma":
      return "RMA";
    case "internal_audit":
      return "Internal audit";
    case "qc_incident":
      return "QC incident";
    case "supplier":
      return "Supplier";
    case "incoming_quality":
      return "Incoming quality";
    case "management_review":
      return "Management review";
    case "process_deviation":
      return "Process deviation";
    case "other":
      return "Other";
    default:
      return source;
  }
}

export async function insertCorrectiveActionActivity(
  client: Pool | PoolClient,
  params: {
    reportId: number;
    actorEmail: string;
    actionType: string;
    fieldName?: string | null;
    oldValue?: string | null;
    newValue?: string | null;
    note?: string | null;
  },
): Promise<void> {
  await client.query(
    `insert into quality.corrective_action_activity
       (corrective_action_report_id, actor_email, action_type, field_name, old_value, new_value, note)
     values ($1, $2, $3, $4, $5, $6, $7)`,
    [
      params.reportId,
      params.actorEmail,
      params.actionType,
      params.fieldName ?? null,
      params.oldValue ?? null,
      params.newValue ?? null,
      params.note ?? null,
    ],
  );
}

export async function recordCorrectiveActionSaveActivities(
  client: Pool | PoolClient,
  params: {
    reportId: number;
    actorEmail: string;
    activities: Array<{
      fieldName: string;
      oldValue: string | null;
      newValue: string | null;
    }>;
  },
): Promise<void> {
  const statusActivities = params.activities.filter((activity) => activity.fieldName === "status");
  const fieldActivities = params.activities.filter((activity) => activity.fieldName !== "status");

  for (const activity of statusActivities) {
    await insertCorrectiveActionActivity(client, {
      reportId: params.reportId,
      actorEmail: params.actorEmail,
      actionType: "status_changed",
      fieldName: activity.fieldName,
      oldValue: activity.oldValue,
      newValue: activity.newValue,
    });
  }

  for (const activity of fieldActivities) {
    await insertCorrectiveActionActivity(client, {
      reportId: params.reportId,
      actorEmail: params.actorEmail,
      actionType: "field_updated",
      fieldName: activity.fieldName,
      oldValue: activity.oldValue,
      newValue: activity.newValue,
    });
  }
}

export async function assertClientFsAccountExists(
  client: Pool | PoolClient,
  clientFsAccountId: number,
): Promise<void> {
  const result = await client.query<{ fs_account_id: string | number }>(
    `select client.data.fs_account_id from client.data where client.data.fs_account_id = $1`,
    [clientFsAccountId],
  );
  if (result.rows.length === 0) {
    throw new Error(`Customer ${clientFsAccountId} was not found.`);
  }
}

export async function assertSalesOrderExists(client: Pool | PoolClient, salesOrderId: number): Promise<void> {
  const result = await client.query<{ id: string | number }>(
    `select public.sales_order.id from public.sales_order where public.sales_order.id = $1`,
    [salesOrderId],
  );
  if (result.rows.length === 0) {
    throw new Error(`Sales order ${salesOrderId} was not found.`);
  }
}

export async function assertPurchaseOrderExists(client: Pool | PoolClient, purchaseOrderId: number): Promise<void> {
  const result = await client.query<{ id: string | number }>(
    `select public.purchase_order.id from public.purchase_order where public.purchase_order.id = $1`,
    [purchaseOrderId],
  );
  if (result.rows.length === 0) {
    throw new Error(`Purchase order ${purchaseOrderId} was not found.`);
  }
}

export async function assertRmaExists(client: Pool | PoolClient, rmaId: number): Promise<void> {
  const result = await client.query<{ id: string | number }>(
    `select quality.rma.id from quality.rma where quality.rma.id = $1`,
    [rmaId],
  );
  if (result.rows.length === 0) {
    throw new Error(`RMA ${rmaId} was not found.`);
  }
}

export async function assertQualityCaseExists(client: Pool | PoolClient, qualityCaseId: number): Promise<void> {
  const result = await client.query<{ id: string | number }>(
    `select quality.quality_case.id from quality.quality_case where quality.quality_case.id = $1`,
    [qualityCaseId],
  );
  if (result.rows.length === 0) {
    throw new Error(`Quality case ${qualityCaseId} was not found.`);
  }
}

export async function assertQualityIncidentExists(
  client: Pool | PoolClient,
  qualityIncidentId: number,
): Promise<void> {
  const result = await client.query<{ id: string | number }>(
    `select public.quality_incident.id from public.quality_incident where public.quality_incident.id = $1`,
    [qualityIncidentId],
  );
  if (result.rows.length === 0) {
    throw new Error(`QC incident ${qualityIncidentId} was not found.`);
  }
}

export async function assertSerialFciExists(client: Pool | PoolClient, serialFciId: number): Promise<void> {
  const result = await client.query<{ id: string | number }>(
    `select public.serial_fci.id from public.serial_fci where public.serial_fci.id = $1`,
    [serialFciId],
  );
  if (result.rows.length === 0) {
    throw new Error(`Device serial record ${serialFciId} was not found.`);
  }
}

export async function assertDeviceIdentityExists(client: Pool | PoolClient, deviceId: number): Promise<void> {
  const result = await client.query<{ id: string | number }>(
    `select device.identity.id from device.identity where device.identity.id = $1`,
    [deviceId],
  );
  if (result.rows.length === 0) {
    throw new Error(`Device identity ${deviceId} was not found.`);
  }
}

export const CORRECTIVE_ACTION_REPORT_SELECT = `
  select quality.corrective_action_report.id,
         quality.corrective_action_report.car_number,
         quality.corrective_action_report.title,
         quality.corrective_action_report.status,
         quality.corrective_action_report.source_type,
         quality.corrective_action_report.opened_at,
         quality.corrective_action_report.target_close_date::text as target_close_date,
         quality.corrective_action_report.closed_at,
         quality.corrective_action_report.reported_by,
         coalesce(
           nullif(trim(reported_user_setting.display_name), ''),
           trim(reported_auth_user.email),
           nullif(trim(quality.corrective_action_report.reported_by), '')
         ) as reported_by_display,
         quality.corrective_action_report.owner_user_id::text as owner_user_id,
         coalesce(
           nullif(trim(owner_user_setting.display_name), ''),
           trim(owner_auth_user.email)
         ) as owner_display,
         quality.corrective_action_report.problem_description,
         quality.corrective_action_report.immediate_containment,
         quality.corrective_action_report.root_cause_analysis,
         quality.corrective_action_report.corrective_action_plan,
         quality.corrective_action_report.implementation_notes,
         quality.corrective_action_report.effectiveness_verification,
         quality.corrective_action_report.continued_review_on::text as continued_review_on,
         quality.corrective_action_report.preventive_action,
         quality.corrective_action_report.client_fs_account_id,
         client.data.fs_account_name as client_name,
         quality.corrective_action_report.sales_order_id,
         coalesce(
           nullif(trim(public.sales_order.customer_purchase_order_number), ''),
           nullif(trim(public.sales_order.number), '')
         ) as sales_order_reference,
         quality.corrective_action_report.quality_incident_id,
         quality.corrective_action_report.quality_event_id,
         quality.quality_event.summary as quality_event_summary,
         quality.corrective_action_report.quality_case_id,
         quality.quality_case.title as quality_case_title,
         quality.corrective_action_report.serial_fci_id,
         quality.corrective_action_report.device_id,
         coalesce(public.serial_fci.device_label_serial, active_device_episode.device_label_serial) as device_label_serial,
         quality.corrective_action_report.closed_by,
         coalesce(
           nullif(trim(closed_user_setting.display_name), ''),
           trim(closed_auth_user.email),
           nullif(trim(quality.corrective_action_report.closed_by), '')
         ) as closed_by_display,
         quality.corrective_action_report.verified_by,
         coalesce(
           nullif(trim(verified_user_setting.display_name), ''),
           trim(verified_auth_user.email),
           nullif(trim(quality.corrective_action_report.verified_by), '')
         ) as verified_by_display,
         quality.corrective_action_report.verified_at,
         quality.corrective_action_report.created_at,
         quality.corrective_action_report.updated_at
    from quality.corrective_action_report
    left join quality.quality_event
      on quality.quality_event.id = quality.corrective_action_report.quality_event_id
    left join quality.quality_case
      on quality.quality_case.id = quality.corrective_action_report.quality_case_id
    left join client.data on client.data.fs_account_id = quality.corrective_action_report.client_fs_account_id
    left join public.sales_order on public.sales_order.id = quality.corrective_action_report.sales_order_id
    left join public.serial_fci on public.serial_fci.id = quality.corrective_action_report.serial_fci_id
    left join public.serial_fci active_device_episode
      on active_device_episode.device_id = quality.corrective_action_report.device_id
     and active_device_episode.archived_at is null
    ${CORRECTIVE_ACTION_USER_JOINS}
`;
